import { Injectable } from '@angular/core';
import {
  firstValueFrom,
  Observable,
  map,
  from,
  catchError,
  of,
  switchMap,
} from 'rxjs';
import {
  DocumentService,
  EmailDocumentService,
} from '@maximizer/core/shared/data-access';
import { EmailDocument } from '@maximizer/core/shared/domain';
import { OutlookService } from './outlook/outlook.service';
import { tryOfficeAsync } from '../utils/outlook/outlook.util';
import { nullIfBlank } from '../utils/string.util';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class EmailService {
  constructor(
    private readonly documentService: DocumentService,
    private readonly emailDocumentService: EmailDocumentService,
    private readonly outlookService: OutlookService,
    private readonly translateService: TranslateService,
  ) {}

  async saveEmailToSingleEntry(
    emlFile: File,
    parentKey: string,
    isCompose = false,
  ): Promise<string | null> {
    const attachmentId = await firstValueFrom(
      this.documentService.createAttachment(
        emlFile,
        'application/octet-stream',
      ),
    );
    if (!attachmentId) return null;

    const document$ = this.buildEmailDocument(
      emlFile,
      parentKey,
      attachmentId,
      isCompose,
    );

    return firstValueFrom(document$).then((document) => {
      if (!document) return null;

      const documentId$ = this.emailDocumentService.create(document);
      return firstValueFrom(documentId$);
    });
  }

  async saveEmailToMultipleEntries(
    emlFile: File,
    parentKeys: string[],
    isCompose = false,
  ): Promise<string[] | null> {
    const documentIds: string[] = [];
    const attachmentId = await firstValueFrom(
      this.documentService.createAttachment(
        emlFile,
        'application/octet-stream',
      ),
    );

    if (!attachmentId) return null;

    for (let i = 0; i < parentKeys.length; i++) {
      const parentKey = parentKeys[i];
      if (!attachmentId) continue;

      const document$ = this.buildEmailDocument(
        emlFile,
        parentKey,
        attachmentId,
        isCompose,
      );
      const document = await firstValueFrom(document$);

      if (document) {
        // binaryFileCleanup for the last document
        const isLast = i === parentKeys.length - 1;
        const documentId$ = this.emailDocumentService.create(document, isLast);
        const documentId = await firstValueFrom(documentId$);
        if (documentId) {
          documentIds.push(documentId);
        }
      }
    }

    return documentIds;
  }

  private buildEmailDocument(
    emlFile: File,
    parentKey: string,
    attachmentId: string,
    isCompose = false,
  ): Observable<EmailDocument | null> {
    const decoded = atob(parentKey);
    const firstToken = decoded.split('\t')[0];
    let type = firstToken.toLowerCase();

    // Map CScase to case for options compatibility
    if (type === 'cscase') {
      type = 'case';
    }

    const entryType = type;
    const item = this.outlookService.mailboxItem;

    // In compose the subject is an Office.Subject object, only readable via getAsync
    const properties$ = isCompose
      ? from(this.getComposeProperties())
      : of({
          subject:
            nullIfBlank(item?.subject) ??
            this.translateService.instant('outlook.email.noSubject'),
          dateTime: item?.dateTimeCreated ?? new Date(),
        });

    return properties$.pipe(
      switchMap(({ subject, dateTime }) =>
        this.emailDocumentService.readEmailDefaultByUserAndType(entryType).pipe(
          map((data) => {
            if (data) {
              return {
                id: null,
                name: subject,
                binaryDataId: attachmentId,
                dateTime: dateTime,
                size: emlFile.size,
                parentKey: parentKey,
                result: data.result,
                category: data.category,
                readAccess: data.readAccess,
                fullAccess: data.fullAccess,
              };
            } else {
              return {
                id: null,
                name: subject,
                binaryDataId: attachmentId,
                dateTime: dateTime,
                size: emlFile.size,
                parentKey: parentKey,
                result: null,
                category: null,
                readAccess: '*',
                fullAccess: '*',
              };
            }
          }),
        ),
      ),
      catchError(() => {
        return of(null);
      }),
    );
  }

  private async getComposeProperties(): Promise<{
    subject: string;
    dateTime: Date;
  }> {
    const item = this.outlookService.mailboxItem;
    const rawSubject = item?.subject?.getAsync
      ? await tryOfficeAsync<string>(item.subject.getAsync, false)
      : null;
    const subject =
      nullIfBlank(rawSubject) ??
      this.translateService.instant('outlook.email.noSubject');
    const dateTime = new Date();

    return { subject, dateTime };
  }
}
